// src/components/BranchCard.tsx
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  MapPin, 
  Phone, 
  Trophy, 
  ArrowRight, 
  Star,
  Building2,
  Clock,
  CheckCircle2
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface BranchCardProps {
  slug: string;
  name: string;
  address: string;
  phone: string;
  image: string;
  availableSports: number;
  isMainBranch?: boolean;
  delay?: number;
}

export const BranchCard = ({
  slug,
  name,
  address,
  phone,
  image,
  availableSports,
  isMainBranch = false,
  delay = 0,
}: BranchCardProps) => {
  const { i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.6, delay }}
      whileHover={{ y: -12 }}
      className="h-full"
    >
      <Link to={`/branches/${slug}`} className="group block h-full">
        <div className="relative h-full rounded-3xl overflow-hidden bg-card border border-border hover:border-primary/60 shadow-xl hover:shadow-2xl hover:shadow-primary/20 transition-all duration-500">
          {/* Main Branch Badge - ENHANCED */}
          {isMainBranch && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="absolute top-0 left-0 right-0 z-20"
            >
              <div className="bg-gradient-to-r from-primary via-primary to-accent py-3 px-4 text-center">
                <div className="flex items-center justify-center gap-2">
                  <Star className="h-4 w-4 text-primary-foreground fill-primary-foreground" />
                  <span className="text-sm font-bold text-primary-foreground tracking-wide">
                    {isRTL ? '⭐ الفرع الرئيسي ⭐' : '⭐ Main Branch ⭐'}
                  </span>
                  <Star className="h-4 w-4 text-primary-foreground fill-primary-foreground" />
                </div>
              </div>
            </motion.div>
          )}

          {/* Image Container with Enhanced Overlay */}
          <div className={`relative h-80 overflow-hidden ${isMainBranch ? 'mt-12' : ''}`}>
            <motion.img
              src={image}
              alt={name}
              className="w-full h-full object-cover"
              loading="lazy"
              whileHover={{ scale: 1.08 }}
              transition={{ duration: 0.7 }}
            />
            
            {/* Multi-layer Gradient Overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-card via-card/80 to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

            {/* Building Icon Badge */}
            <motion.div
              whileHover={{ scale: 1.2, rotate: 360 }}
              transition={{ duration: 0.6 }}
              className={`absolute top-6 ${isRTL ? "right-6" : "left-6"} p-4 rounded-2xl bg-primary/95 backdrop-blur-sm border border-primary/40 shadow-2xl`}
            >
              <Building2 className="h-7 w-7 text-primary-foreground" />
            </motion.div>

            {/* Status Badge */}
            <div className={`absolute top-6 ${isRTL ? "left-6" : "right-6"} px-4 py-2 rounded-full bg-green-500/90 backdrop-blur-xl border border-green-500/40 shadow-lg`}>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
                <span className="text-xs font-semibold text-white">
                  {isRTL ? "مفتوح الآن" : "Open Now"}
                </span>
              </div>
            </div>

            {/* Sports Count Badge */}
            <div className={`absolute bottom-6 ${isRTL ? "right-6" : "left-6"}`}>
              <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-card/90 backdrop-blur-xl border border-border/50 shadow-lg">
                <Trophy className="h-4 w-4 text-primary" />
                <span className="text-sm font-semibold">
                  {availableSports} {isRTL ? "رياضة" : "Sports"}
                </span>
              </div>
            </div>
          </div>

          {/* Enhanced Content */}
          <div className={`p-8 ${isRTL ? "text-right" : "text-left"}`}>
            {/* Branch Name */}
            <h3 className="text-2xl font-bold mb-6 group-hover:text-primary transition-colors duration-300">
              {name}
            </h3>

            {/* Info Grid */}
            <div className="space-y-4 mb-6">
              {/* Address */}
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-lg bg-primary/10 border border-primary/20 flex-shrink-0">
                  <MapPin className="h-4 w-4 text-primary" />
                </div>
                <div className="flex-1">
                  <p className="text-sm text-muted-foreground font-medium leading-relaxed">
                    {address}
                  </p>
                </div>
              </div>

              {/* Phone */}
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary/10 border border-primary/20">
                  <Phone className="h-4 w-4 text-primary" />
                </div>
                <a 
                  href={`tel:${phone}`} 
                  className="text-sm text-muted-foreground hover:text-primary transition-colors font-medium"
                  onClick={(e) => e.stopPropagation()}
                >
                  {phone}
                </a>
              </div>

              {/* Hours */}
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary/10 border border-primary/20">
                  <Clock className="h-4 w-4 text-primary" />
                </div>
                <span className="text-sm text-muted-foreground font-medium">
                  {isRTL ? "السبت - الخميس: 8 ص - 8 م" : "Sat - Thu: 8 AM - 8 PM"}
                </span>
              </div>
            </div>

            {/* Features */}
            <div className="flex flex-wrap gap-2 mb-6">
              {[
                { icon: CheckCircle2, label: isRTL ? "معتمد" : "Certified" },
                { icon: Trophy, label: isRTL ? "احترافي" : "Professional" },
              ].map((feature, idx) => (
                <div
                  key={idx}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/20 text-xs font-medium text-primary"
                >
                  <feature.icon className="h-3 w-3" />
                  {feature.label}
                </div>
              ))}
            </div>

            {/* Footer with CTA */}
            <div className="flex items-center justify-between pt-6 border-t border-border/50">
              <span className="text-sm text-muted-foreground font-medium">
                {isRTL ? "تفاصيل الفرع" : "Branch Details"}
              </span>

              <motion.div
                whileHover={{ x: isRTL ? -8 : 8 }}
                className="text-primary font-semibold flex items-center gap-2"
              >
                {isRTL ? "اكتشف" : "Explore"}
                <ArrowRight className={`h-5 w-5 ${isRTL ? "rotate-180" : ""}`} />
              </motion.div>
            </div>
          </div>

          {/* Shine Effect */}
          <div
            className={`absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent ${
              isRTL
                ? "translate-x-full group-hover:-translate-x-full"
                : "-translate-x-full group-hover:translate-x-full"
            } transition-transform duration-1000`}
          />
        </div>
      </Link>
    </motion.div>
  );
};
